import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Filter, ShieldAlert, Clock } from "lucide-react";
import ReworkIcon from "./icons/ReworkIcon";
import { formatCurrency } from "@/components/dashboard/utils";
import { isItemBlocked, isItemOverdue, startOfToday } from "@/components/StagePage/stageFlags";

/* ── Pipeline rail (header redesign "Layout 1", 2-Nov-2026) ─────────────────
   Row 2 of the ERP header, and the only thing on it: Dashboard is the rail's
   first cell (the "Stacked" tile — icon over the word), then one tile per
   visible stage. Spec: plans/pipeline-header-redesign.md + the mock beside it.

   · Track (.rl-rail-glass, 3-Nov-2026) — a solid card fill wearing the
     dashboard hero cards' rim + shadow; no transparency, no blur. Never
     pressed in; a track is not a state. The drop shadow lives on
     .rl-rail-shell because the rail's own clip-path would cut it off.
   · Every stage tile always wears a faint chevron in its stage colour, and the
     chevrons interlock (16px point/notch overhang, 4px visible gap), so the bar
     reads as a chain of arrows. No step numbers, no › separators.
   · The thumb is a STRETCHY ARROW: the leading edge springs ahead, the
     trailing edge follows. Dashboard / first / last stage use a no-overshoot
     ease so the arrow never springs past the rail's rounded ends (the rail's
     clip-path masks whatever is left).
   · GPU-only motion (5-Nov-2026, Tushar: lag on busy PCs). The arrow is three
     pieces moved by Web Animations transform keyframes, the colour change is
     an opacity cross-fade, and a click starts the glide BEFORE React builds
     the next page. Nothing animates a custom property, width or colour any
     more, so the rail is never repainted mid-glide. (Was: --l / --r custom
     properties driving left/width, which repainted the whole rail every frame
     and could not start until the page render finished.)
   · Hero mode on "/": tiles grow and add value (can_view_value only) + a 7-day
     sparkline; they collapse back on any other route.

   Timing: StagePage holds its heavy row commit for HOLD_MS (520) so the page
   swap lands after the 480ms glide. Retune HOLD_MS with GLIDE_MS.

   Separate class names (.rl-flow-*) on purpose: Ecotile and Maintenance still
   use .pipeline-nav / .pipe-thumb and must not change. */

// Chevron point overhang (= App.css --ov on 56px tiles). Only the POINT
// overhangs (the notch sits inside the tile), so the thumb spans
// [tile left, tile right + point]. The thumb only ever rests on a stage in
// compact mode (the hero mode belongs to Dashboard), so this is the only size
// its geometry needs; the point itself is drawn with the live --ov.
const OV_COMPACT = 20;

// The arrow is three GPU pieces (5-Nov-2026, lag on busy PCs): a left end
// (notch or rounded), a right end (point or rounded) and a plain middle that is
// stretched with scaleX. The middle is the same colour all the way across
// (the gloss is top-to-bottom only), so stretching it changes nothing you can
// see, while the ends keep their exact shape. CAP must stay >= the hero point
// (28px) + 8 so a point never pokes past its own piece.
const CAP = 36;
const BASE = 100;                       // the middle piece's drawn width before scaleX

// CSS cubic-bezier maths, so the GPU keyframes trace the same curves the old
// CSS transitions did (Newton-Raphson, with a bisection fallback).
function cubicBezier(x1, y1, x2, y2) {
  const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
  const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
  const sx = (t) => ((ax * t + bx) * t + cx) * t;
  const sy = (t) => ((ay * t + by) * t + cy) * t;
  const dx = (t) => (3 * ax * t + 2 * bx) * t + cx;
  return (x) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 8; i++) {
      const e = sx(t) - x;
      if (Math.abs(e) < 1e-6) return sy(t);
      const d = dx(t);
      if (Math.abs(d) < 1e-6) break;
      t -= e / d;
    }
    let lo = 0, hi = 1;
    t = x;
    for (let i = 0; i < 40; i++) {
      const v = sx(t);
      if (Math.abs(v - x) < 1e-6) break;
      if (v < x) lo = t; else hi = t;
      t = (lo + hi) / 2;
    }
    return sy(t);
  };
}
// Same timings as the CSS-transition version (2-Nov-2026).
const LEAD_SPRING = cubicBezier(0.3, 1.3, 0.5, 1);    // leading edge springs ahead
const LEAD_FLAT   = cubicBezier(0.22, 0.7, 0.3, 1);   // ...but never past the rail's rounded ends
const TAIL_EASE   = cubicBezier(0.55, 0, 0.2, 1);     // trailing edge follows
const LEAD_MS = 300;
const TAIL_DELAY = 40;
const TAIL_MS = 440;
const GLIDE_MS = TAIL_DELAY + TAIL_MS;                 // 480: StagePage's HOLD_MS (520) covers it
const FADE_MS = 300;                                   // colour cross-fade
const STEP_MS = 8;                                     // keyframe spacing (half a 60 Hz frame)

// Where the two edges are, t ms into a glide.
function edgesAt(g, t) {
  const pl = g.lead(Math.min(t / LEAD_MS, 1));
  const pt = TAIL_EASE(Math.min(Math.max((t - TAIL_DELAY) / TAIL_MS, 0), 1));
  const pL = g.fwd ? pt : pl;
  const pR = g.fwd ? pl : pt;
  return [g.L0 + (g.L1 - g.L0) * pL, g.R0 + (g.R1 - g.R0) * pR];
}
const px = (n) => `${Math.round(n * 100) / 100}px`;
const pieceTransforms = (L, R) => {
  const x0 = L + CAP - 1;                // the middle tucks 1px under each end: no seam
  const x1 = R - CAP + 1;
  return [
    `translateX(${px(L)})`,
    `translateX(${px(x0)}) scaleX(${Math.round((Math.max(x1 - x0, 0) / BASE) * 10000) / 10000})`,
    `translateX(${px(R - CAP)})`,
  ];
};

// Today's dashboard button colour, under the rainbow mesh.
const HOME_COLOR = "#fbbf24";

function Sparkline({ data, color }) {
  if (!data || data.length < 2) return null;
  const w = 58, h = 16;
  const min = Math.min(...data), max = Math.max(...data);
  const span = max - min || 1;
  const pts = data.map((v, i) =>
    `${((i / (data.length - 1)) * w).toFixed(1)},${(h - 2 - ((v - min) / span) * (h - 4)).toFixed(1)}`
  ).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" className="rl-flow-spark">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function PipelineRail({
  stages,                // visibleNav: Dashboard ("/") + role-filtered stages
  pathname,
  onNavigate,
  lensItems,             // items already scoped by the division lens
  getStageCount,
  getFilteredStageCount,
  reworkCount,
  lastAdvanceInfo,
  showValue,
  trend,
  reduced,               // Performance Mode or prefers-reduced-motion → snap
}) {
  const railRef = useRef(null);
  const thumbRef = useRef(null);       // the arrow you see
  const fadeRef = useRef(null);        // its twin on top, for the colour cross-fade
  const meshRef = useRef(null);        // Dashboard's rainbow mesh, pinned on its tile
  const cellRefs = useRef(new Map());
  // Where the arrow is heading, and the glide in flight (if any).
  const motionRef = useRef({ idx: null, L: 0, R: 0, color: HOME_COLOR, glide: null, fade: null, anims: [] });
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  const { stageCells, home, cells } = useMemo(() => {
    const st = stages.filter(s => s.path !== "/");
    const h = stages.find(s => s.path === "/");
    return { stageCells: st, home: h, cells: h ? [h, ...st] : st };
  }, [stages]);
  const activeIdx = cells.findIndex(s => s.path === pathname);
  const hero = pathname === "/";

  // Per-stage blocked / overdue / value, lens-scoped like the counts beside them.
  const perStage = useMemo(() => {
    const today = startOfToday();
    const out = {};
    for (const i of lensItems) {
      const s = out[i.stage] || (out[i.stage] = { blocked: 0, overdue: 0, value: 0 });
      if (isItemBlocked(i)) s.blocked += 1;
      if (isItemOverdue(i, today)) s.overdue += 1;
      s.value += i.value_usd || 0;
    }
    return out;
  }, [lensItems]);

  // Move the arrow to cells[idx]. Everything that moves is a transform or an
  // opacity animated with the Web Animations API, so the GPU runs it on its own
  // even while React is busy building the next page. Nothing in the rail is
  // repainted per frame.
  const go = useCallback((idx, instant) => {
    const A = thumbRef.current;
    const B = fadeRef.current;
    const mesh = meshRef.current;
    if (!A || !B) return;
    const m = motionRef.current;
    const stopAnims = () => {
      m.anims.forEach(a => a.cancel());
      m.anims = [];
      if (m.fade) { m.fade.anim.cancel(); m.fade = null; }
    };
    const target = idx >= 0 ? cellRefs.current.get(cells[idx].path) : null;
    if (!target) {
      stopAnims();
      m.glide = null;
      m.idx = null;
      A.classList.remove("on");
      if (mesh) mesh.classList.remove("on");
      return;
    }
    const s = cells[idx];
    const isHome = s.path === "/";
    const isFirst = !isHome && idx === (home ? 1 : 0);
    const isLast = !isHome && idx === cells.length - 1;
    const L1 = target.offsetLeft;
    const R1 = target.offsetLeft + target.offsetWidth + (isHome || isLast ? 0 : OV_COMPACT);
    const color = isHome ? HOME_COLOR : (s.color || HOME_COLOR);
    const shown = A.classList.contains("on");

    // Already there (or already heading there)? Nothing to do.
    if (!instant && shown && m.idx === idx && Math.abs(m.L - L1) < 0.5 && Math.abs(m.R - R1) < 0.5) return;
    // Same tile, nudged a few px (tiles settle by ~2px when the Dashboard's
    // tall tiles shrink back): don't restart the glide, just land exactly
    // once it is done.
    if (!instant && shown && m.idx === idx && Math.abs(m.L - L1) < 6 && Math.abs(m.R - R1) < 6) {
      const a = m.anims[0];
      if (a && a.playState !== "finished") {
        a.finished.then(() => { if (m.idx === idx) goRef.current(idx, true); }, () => {});
        return;
      }
      instant = true;
    }

    // Where the arrow is right now, and which colour is showing. Read the
    // animations' own clocks: a glide only starts on the next frame, which can
    // be late on a busy PC, so the wall clock would run ahead of the picture.
    const clock = (a, done) => {
      if (!a || a.playState === "finished") return done;
      const t = a.currentTime;
      return t == null ? 0 : Number(t);
    };
    const [L0, R0] = m.glide ? edgesAt(m.glide, clock(m.anims[0], GLIDE_MS)) : [m.L, m.R];
    let fromColor = m.color;
    if (m.fade) fromColor = clock(m.fade.anim, FADE_MS) >= FADE_MS / 2 ? m.fade.to : m.fade.from;
    const fromIdx = m.idx;
    stopAnims();

    // Ends switch shape at the start of the move, as before.
    for (const t of [A, B]) {
      t.classList.toggle("home", isHome);
      t.classList.toggle("first", isFirst);
      t.classList.toggle("last", isLast);
    }
    const rest = pieceTransforms(L1, R1);
    for (const t of [A, B]) [...t.children].forEach((el, i) => { el.style.transform = rest[i]; });

    const snap = instant || reduced || fromIdx === null || !shown;
    if (mesh) {
      const hc = home ? cellRefs.current.get("/") : null;
      if (hc) { mesh.style.left = `${hc.offsetLeft}px`; mesh.style.width = `${hc.offsetWidth}px`; }
      if (snap) mesh.style.transition = "none";
      mesh.classList.toggle("on", isHome);
      if (snap) { void mesh.offsetWidth; mesh.style.transition = ""; }
    }
    m.idx = idx; m.L = L1; m.R = R1; m.color = color;

    if (snap) {
      m.glide = null;
      A.style.setProperty("--c", color);
      B.style.setProperty("--c", color);
      A.classList.add("on");       // first show fades in (opacity, on the GPU)
      return;
    }

    // The stretchy arrow: the leading edge leaps, the trailing edge follows.
    const g = {
      L0, R0, L1, R1,
      fwd: idx > fromIdx,
      lead: isHome || isFirst || isLast ? LEAD_FLAT : LEAD_SPRING,
    };
    const frames = [[], [], []];
    for (let t = 0; ; t = Math.min(t + STEP_MS, GLIDE_MS)) {
      const [L, R] = edgesAt(g, t);
      pieceTransforms(L, R).forEach((tf, i) => frames[i].push({ offset: t / GLIDE_MS, transform: tf }));
      if (t === GLIDE_MS) break;
    }
    const opts = { duration: GLIDE_MS, easing: "linear" };
    for (const t of [A, B]) [...t.children].forEach((el, i) => m.anims.push(el.animate(frames[i], opts)));
    m.glide = g;

    // Colour: the twin on top fades in wearing the new colour, then hands it
    // to the arrow underneath and steps away (both in one frame, so no flash).
    A.style.setProperty("--c", fromColor);
    B.style.setProperty("--c", color);
    if (fromColor !== color) {
      const anim = B.animate([{ opacity: 0 }, { opacity: 1 }], { duration: FADE_MS, easing: "ease", fill: "forwards" });
      const fade = { from: fromColor, to: color, anim };
      m.fade = fade;
      anim.onfinish = () => {
        if (m.fade !== fade) return;
        A.style.setProperty("--c", color);
        anim.cancel();
        m.fade = null;
      };
    }
  }, [cells, home, reduced]);

  const goRef = useRef(go);
  goRef.current = go;
  const activeIdxRef = useRef(activeIdx);
  activeIdxRef.current = activeIdx;

  // Clicks move the arrow and flip the white text on the clicked tile NOW, then
  // hand the route change to React one frame later. Building the next page can
  // hold the main thread for a while on a busy PC; by then the glide is already
  // running on the GPU, so it no longer waits for (or stutters with) the page.
  const pendingRef = useRef(null);
  const goTo = (path) => {
    const idx = cells.findIndex(c => c.path === path);
    if (reduced || idx < 0 || path === pathnameRef.current) { onNavigate(path); return; }
    go(idx, false);
    cellRefs.current.forEach((el, p) => el.classList.toggle("active", p === path));
    pendingRef.current = path;
    let fired = false;
    const fire = () => {
      if (fired) return;
      fired = true;
      onNavigate(path);
      // If the route never changes (nothing should stop it, but just in case),
      // put the rail back to the page we are really on.
      setTimeout(() => {
        if (pendingRef.current !== path || pathnameRef.current === path) return;
        pendingRef.current = null;
        cellRefs.current.forEach((el, p) => el.classList.toggle("active", p === pathnameRef.current));
        goRef.current(cells.findIndex(c => c.path === pathnameRef.current), false);
      }, 1500);
    };
    requestAnimationFrame(() => setTimeout(fire, 0));
    setTimeout(fire, 100);   // backstop if frames are paused
  };

  // Mount snaps; route changes glide (a click has usually started the glide
  // already, and go() then does nothing). Layout effect = before paint. A rAF +
  // fonts.ready top-up re-snaps on mount once late fonts settle the tile widths.
  const mountedRef = useRef(false);
  useLayoutEffect(() => {
    const first = !mountedRef.current;
    mountedRef.current = true;
    pendingRef.current = null;
    go(activeIdx, first);
    if (!first) return undefined;
    const raf = requestAnimationFrame(() => go(activeIdx, true));
    let live = true;
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { if (live) goRef.current(activeIdxRef.current, true); });
    return () => { live = false; cancelAnimationFrame(raf); };
  }, [pathname, stages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // The tiles grow/shrink (height + --ov) when entering or leaving Dashboard,
  // which moves them by a pixel or two. When that settles, line the arrow up.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;
    const onEnd = (e) => {
      if (e.propertyName === "height" && e.target.classList?.contains("rl-flow-cell")) {
        goRef.current(activeIdxRef.current, false);
      }
    };
    rail.addEventListener("transitionend", onEnd);
    return () => rail.removeEventListener("transitionend", onEnd);
  }, []);

  // Stop any glide if the rail unmounts.
  useEffect(() => () => {
    const m = motionRef.current;
    m.anims.forEach(a => a.cancel());
    if (m.fade) m.fade.anim.cancel();
  }, []);

  // Tile widths only change when the rail itself gets WIDER or NARROWER
  // (window, zoom). SKIP the observer's initial callback: it fires on observe()
  // and would land a frame into a glide and SNAP it (the division-rail lesson).
  // Ignore HEIGHT-only changes too: the hero grow/shrink on entering/leaving
  // "/" resizes the rail every frame, and re-placing then snapped the arrow
  // instead of letting it slide to or from Dashboard (bug, 2-Nov-2026).
  useEffect(() => {
    const rail = railRef.current;
    if (!rail || typeof ResizeObserver === "undefined") return undefined;
    let primed = false;
    let lastWidth = rail.offsetWidth;
    const ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect?.width ?? rail.offsetWidth);
      if (!primed) { primed = true; lastWidth = w; return; }
      if (w === lastWidth) return;
      lastWidth = w;
      goRef.current(activeIdxRef.current, true);
    });
    ro.observe(rail);
    return () => ro.disconnect();
  }, []);

  const firstStagePath = stageCells[0]?.path;
  const lastStagePath = stageCells[stageCells.length - 1]?.path;

  return (
    <div className="rl-rail-shell">
    <div
      ref={railRef}
      className={`rl-flow-rail rl-rail-glass${hero ? " hero" : ""}`}
      role="navigation"
      aria-label="Pipeline"
      data-testid="pipeline-rail"
    >
      <div ref={thumbRef} className="rl-flow-thumb" aria-hidden="true"><i className="l" /><i className="m" /><i className="r" /></div>
      <div ref={fadeRef} className="rl-flow-thumb fade" aria-hidden="true"><i className="l" /><i className="m" /><i className="r" /></div>
      <div ref={meshRef} className="rl-flow-homemesh" aria-hidden="true"><i className="rl-flow-mesh" /></div>
      {cells.map((s) => {
        const isActive = s.path === pathname;
        const setRef = (el) => { if (el) cellRefs.current.set(s.path, el); else cellRefs.current.delete(s.path); };
        const testid = `nav-${s.path.replace("/", "") || "dashboard"}`;
        const Icon = s.icon;

        if (s.path === "/") {
          return (
            <button
              key={s.path}
              ref={setRef}
              type="button"
              onClick={() => goTo("/")}
              className={`rl-flow-cell rl-flow-home${isActive ? " active" : ""}`}
              aria-current={isActive ? "page" : undefined}
              data-testid={testid}
            >
              <span className="rl-flow-stack">
                <Icon className="rl-flow-home-ico" strokeWidth={1.9} />
                <span>{s.label}</span>
              </span>
            </button>
          );
        }

        const count = getStageCount(s.stage);
        const filterCount = getFilteredStageCount(s.stage);
        const showAdvance = lastAdvanceInfo && lastAdvanceInfo.toStage === s.stage;
        const archive = s.stage === "Completed";
        const st = perStage[s.stage] || { blocked: 0, overdue: 0, value: 0 };
        const cls = [
          "rl-flow-cell",
          s.path === firstStagePath && "first",
          s.path === lastStagePath && "last",
          archive && "archive",
          isActive && "active",
        ].filter(Boolean).join(" ");
        return (
          <button
            key={s.path}
            ref={setRef}
            type="button"
            onClick={() => goTo(s.path)}
            className={cls}
            style={{ "--stage": s.color }}
            aria-current={isActive ? "page" : undefined}
            data-testid={testid}
          >
            <span className="rl-flow-c1">
              <Icon className="w-[15px] h-[15px] shrink-0" />
              <span className="truncate">{s.label}</span>
            </span>
            <span className="rl-flow-c2">
              {/* Plain count — REPLACED by the filter-match chip while a global
                  (or due) filter is narrowing this stage: one token, not two. */}
              {count !== null && filterCount === null && (
                <span
                  key={showAdvance ? `pulse-${lastAdvanceInfo.time}` : "static"}
                  className={`rl-flow-num${showAdvance ? " tab-count-advanced" : ""}`}
                >
                  {count}
                </span>
              )}
              {filterCount !== null && (
                <span
                  className="pipe-filter-chip rl-flow-filter"
                  style={isActive
                    ? { background: "rgba(255,255,255,0.24)", color: "#fff" }
                    : filterCount > 0
                      ? { background: `${s.color}26`, color: s.color }
                      : { background: "hsl(var(--muted-foreground) / 0.12)", color: "hsl(var(--muted-foreground))" }}
                  title={`${filterCount} matching ${filterCount === 1 ? "item" : "items"} in ${s.label}`}
                  data-testid={`nav-filter-tag-${s.stage.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Filter className="w-3 h-3" strokeWidth={2.6} />
                  {filterCount}
                </span>
              )}
              {!archive && st.blocked > 0 && (
                <span className="rl-flow-chip b" title={`${st.blocked} blocked`} data-testid={`nav-blocked-${s.path.replace("/", "")}`}>
                  <ShieldAlert className="w-[11px] h-[11px]" strokeWidth={2.4} />{st.blocked}
                </span>
              )}
              {!archive && st.overdue > 0 && (
                <span className="rl-flow-chip o" title={`${st.overdue} overdue`} data-testid={`nav-overdue-${s.path.replace("/", "")}`}>
                  <Clock className="w-[11px] h-[11px]" strokeWidth={2.4} />{st.overdue}
                </span>
              )}
              {s.stage === "Manufacturing" && reworkCount > 0 && (
                <span
                  className="rl-flow-chip rw"
                  title={`${reworkCount} rework item${reworkCount === 1 ? "" : "s"}`}
                  data-testid="nav-rework-badge"
                >
                  <ReworkIcon className="w-[11px] h-[11px]" strokeWidth={2.4} />{reworkCount}
                </span>
              )}
            </span>
            <span className="rl-flow-c3" aria-hidden={!hero}>
              {archive ? (
                <span>Shipped and closed</span>
              ) : (
                <>
                  {showValue && <span className="tabular-nums">{formatCurrency(st.value)}</span>}
                  <Sparkline data={trend?.series?.[s.stage]} color={isActive ? "#fff" : s.color} />
                </>
              )}
            </span>
          </button>
        );
      })}
    </div>
    </div>
  );
}
